import { ceil, toInteger } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { useRef } from 'react';
import message from '@meitu/saas-ui/es/message';
import { getFramework, getTargetType, useIdentity, useMoveEventListener } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { ITouchKitEvent } from '@/utils/event-kit';
import { ILayerProps, MAX_STAGE_WIDTH as MAX_STAGE_SIZE, MIN_STAGE_WIDTH as MIN_STAGE_SIZE } from '@/version-types';
import { CropStagePlugin } from '../controller';
import { useStageCropStore } from '../models/crop-stage-state';
import { getOperateStageStyle, setScaleStageStyle } from '../utils';

const dataTypes = ['crop-bc', 'crop-rc', 'crop-lc', 'crop-tc'];

// 不能超过最大值和最小值
const adjustSize = (size: number, text: string) => {
    if (size < MIN_STAGE_SIZE) {
        message.error(text);
        return 50;
    } else if (size > MAX_STAGE_SIZE) {
        return MAX_STAGE_SIZE;
    }

    return size;
};

export const useSizeChangeOperate = (scale: number) => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const originIndex = framework.stage.getActiveOriginIndex();
    const getLayers = () => framework.getLayers(originIndex);
    const getMainLayer = () => framework.getMainLayer(originIndex);
    const cropController = framework.getInstance(CropStagePlugin);

    const { $t } = getLocaleStore();

    const { isStageAutoCrop, setIsCropStageOperating } = useStageCropStore()((state) => state);
    const lastMainLayerRef = useRef<ILayerProps | null>(null);
    const lastLayersRef = useRef<ILayerProps[] | null>(null);

    useMoveEventListener((e: ITouchKitEvent) => {
        const target = e.target;
        const dataType = target.getAttribute('data-type');

        if (!getTargetType(target)) return;

        if (!dataTypes.includes(dataType || '')) return;

        if (e.isStart) {
            const mainLayer = getMainLayer();
            const layers = getLayers();
            lastMainLayerRef.current = cloneDeep(mainLayer);
            lastLayersRef.current = cloneDeep(layers);
            return;
        }

        if (!lastMainLayerRef.current || !lastLayersRef.current || !dataType) {
            return;
        }
        setIsCropStageOperating(true);

        const { left, top, width, height } = lastMainLayerRef.current;
        const params = {
            width,
            height,
            left,
            top,
        };

        const { displacementX, displacementY } = e;
        const moveDistance = {
            x: toInteger(displacementX / scale),
            y: toInteger(displacementY / scale),
        };

        if (dataType === 'crop-bc') {
            params.height = adjustSize(
                toInteger(params.height + moveDistance.y),
                $t((v) => v.stageManager.canvasLimitScope),
            );
        } else if (dataType === 'crop-rc') {
            params.width = adjustSize(
                toInteger(params.width + moveDistance.x),
                $t((v) => v.stageManager.canvasLimitScope),
            );
        } else if (dataType === 'crop-lc') {
            const val = toInteger(params.width - moveDistance.x);
            params.width = ceil(
                adjustSize(
                    val,
                    $t((v) => v.stageManager.canvasLimitScope),
                ),
            );

            params.left = params.left - ceil(moveDistance.x - (params.width - val));
        } else if (dataType === 'crop-tc') {
            const val = toInteger(params.height - moveDistance.y);
            params.height = ceil(
                adjustSize(
                    val,
                    $t((v) => v.stageManager.canvasLimitScope),
                ),
            );

            params.top = params.top - ceil(moveDistance.y - (params.height - val));
        }

        // 固定比例裁剪的时候拖动也要固定比例
        if ((dataType === 'crop-rc' || dataType === 'crop-lc') && !isStageAutoCrop) {
            params.height = (params.width / width) * height;
            params.top = params.top - (height - params.height) / 2;

            if (params.height > MAX_STAGE_SIZE) {
                message.error($t((v) => v.stageManager.canvasLimitScope));
                params.top = params.top - (params.height - MAX_STAGE_SIZE) / 2;
                params.height = MAX_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }

            if (params.height < MIN_STAGE_SIZE) {
                message.error($t((v) => v.stageManager.canvasLimitScope));
                params.top = params.top - (params.height - MIN_STAGE_SIZE) / 2;
                params.height = MIN_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }
        }

        if ((dataType === 'crop-bc' || dataType === 'crop-tc') && !isStageAutoCrop) {
            params.width = (params.height / height) * width;
            params.left = params.left - (width - params.width) / 2;

            if (params.width > MAX_STAGE_SIZE) {
                message.error($t((v) => v.stageManager.canvasLimitScope));
                params.left = params.left - (params.width - MAX_STAGE_SIZE) / 2;
                params.width = MAX_STAGE_SIZE;
                params.top = params.top - (params.height - (params.width / width) * height);
                params.height = (params.width / width) * height;
            }

            if (params.height < MIN_STAGE_SIZE) {
                message.error($t((v) => v.stageManager.canvasLimitScope));
                params.top = params.top - (params.height - MIN_STAGE_SIZE) / 2;
                params.height = MIN_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }
        }

        const newLayerSize = cropController.moveChangeStageSizeOperate(
            params,
            cloneDeep(lastMainLayerRef.current),
            cloneDeep(lastLayersRef.current),
            dataType,
            isStageAutoCrop,
        );

        if (newLayerSize) {
            const moveDirection = {
                x: e.displacementX ? e.displacementX / Math.abs(e.displacementX) : 1,
                y: e.displacementY ? e.displacementY / Math.abs(e.displacementY) : 1,
            };
            setScaleStageStyle(
                newLayerSize.width,
                newLayerSize.height,
                scale,
                dataType,
                isStageAutoCrop,
                moveDirection,
                {
                    width,
                    height,
                },
                e.isEnd,
            );
            getOperateStageStyle(
                newLayerSize.width,
                newLayerSize.height,
                scale,
                dataType,
                isStageAutoCrop,
                moveDirection,
                {
                    width,
                    height,
                },
                e.isEnd,
            );
        }
        if (e.isEnd) {
            lastMainLayerRef.current = null;
            lastLayersRef.current = null;
            setIsCropStageOperating(false);
        }
    });
};
